import { AnimatePresence, type AnimationProps, motion, useReducedMotion } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, type MessageDescriptor, useIntl } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";

import { handleDropped, removeMediaItem, setCbctFiles } from "~/actions/dashboard";
import { addUserActionNotification } from "~/actions/user_notification";
import FileUploader from "~/common/async/FileUploader";
import { PATIENT_MAX_FILESIZE } from "~/common/constants";
import { convertHeicToJpeg, isHeicFile } from "~/common/heic";
import { Download, FileText, Loader, Trash2 } from "~/common/redesign-v1/icons";
import { convertBytesToMB } from "~/common/utils";
import { fetchCbctUploadLinks, isCbctArchive, validateCbctFiles } from "~/components/patient/patient_shared/cbct-upload";
import { FileDropzone } from "~/components/redesign-v1/patient-form/native/file-dropzone";
import { CenteredDialog } from "~/components/ui/centered-dialog";
import { LabelRequiredIndicator } from "~/components/ui/label";
import { API_PATIENT_IMAGE } from "~/config";
import { selectClinics } from "~/slices/clinics";
import { selectInstructions } from "~/slices/instructions";
import type { RootState } from "~/store";
import type { TCorrectionMediaSemantics, TPatientOptionalMedia } from "~/types/patient";

const mapStateToProps = (state: RootState) => ({
  media: state.media,
  mediaS3: state.media_s3,
  clinics: selectClinics(state),
  patientClinic: state.patient && state.patient.clinic,
  instructions: selectInstructions(state),
});

const mapDispatchToProps = {
  handleDropped,
  removeMediaItem,
  addUserActionNotification,
  setCbctFiles,
};

type PowerCapFileUploadProps = ConnectedProps<typeof connector> & {
  required?: boolean;
  isVisible?: boolean;
  acceptsCbct?: boolean;
  titleId?: MessageDescriptor["id"];
  subtitleId?: MessageDescriptor["id"];
  patientId?: number;
  existingFiles?: TPatientOptionalMedia[];
  children?: React.ReactNode;
};

/** Removing an upload from Redux before form submission keeps it out of the patient payload. */
function PowerCapFileUpload({
  media,
  mediaS3,
  clinics,
  patientClinic,
  instructions,
  handleDropped,
  removeMediaItem,
  addUserActionNotification,
  setCbctFiles,
  required,
  isVisible = true,
  acceptsCbct = false,
  titleId,
  subtitleId,
  patientId = 0,
  existingFiles = [],
  children,
}: PowerCapFileUploadProps) {
  const intl = useIntl();
  const [slotIds, setSlotIds] = useState<string[]>([]);
  const [progressById, setProgressById] = useState<Record<string, number>>({});
  // Pending photos use a local preview dialog; other files keep the generic file row.
  const [previewSlotId, setPreviewSlotId] = useState<string | null>(null);
  const [detailsById, setDetailsById] = useState<Record<string, { size: number; uploadedAt: number }>>({});
  const [cbctPending, setCbctPending] = useState<{ name: string; size: number; uploadedAt: number }[]>([]);
  const [cbctDetails, setCbctDetails] = useState<Record<string, { size: number; uploadedAt: number }>>({});
  const [dropzoneDragging, setDropzoneDragging] = useState(false);
  const cbctFilesRef = useRef<string[]>([]);
  const nextSlot = useRef(0);

  function uploadFinished(id: string) {
    setProgressById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function removeFailedSlot(id: string) {
    uploadFinished(id);
    setSlotIds((prev) => prev.filter((slotId) => slotId !== id));
  }

  function acceptDrop(file: File) {
    const id = `recipe_${nextSlot.current++}`;
    setSlotIds((prev) => [...prev, id]);
    setProgressById((prev) => ({ ...prev, [id]: 0 }));
    setDetailsById((prev) => ({ ...prev, [id]: { size: file.size, uploadedAt: Date.now() } }));
    handleDropped(
      file,
      patientId,
      "recipe",
      id,
      undefined,
      () => uploadFinished(id),
      PATIENT_MAX_FILESIZE,
      (percent) => setProgressById((prev) => (id in prev ? { ...prev, [id]: percent } : prev)),
      // `handleDropped` reports the error; this callback removes its pending row.
      () => removeFailedSlot(id),
    );
  }

  function cbctClinicId(): number | null {
    if (instructions?.clinic_id) {
      return clinics?.find((clinic) => clinic.clinic_id === instructions.clinic_id)?.clinic_id ?? null;
    }

    return patientClinic?.clinic_id ?? null;
  }

  async function uploadCbct(files: File[]) {
    const error = validateCbctFiles(files);
    if (error) {
      addUserActionNotification({ message: error, level: "error" });
      return;
    }

    const pending = files.map((file) => ({ name: file.name, size: file.size, uploadedAt: Date.now() }));
    setCbctPending((prev) => [...prev, ...pending]);
    $("#submit-pacient-btn, #save-pacient-btn").prop("disabled", true);

    const done = () => {
      $("#submit-pacient-btn, #save-pacient-btn").prop("disabled", false);
      setCbctPending((prev) => prev.filter((entry) => !pending.some((item) => item.name === entry.name)));
    };

    if (!mediaS3.uuid) {
      done();
      addUserActionNotification({ message: "E_CONNECTION_FAILED", level: "error" });
      return;
    }

    let links: Record<string, string>;
    try {
      links = await fetchCbctUploadLinks(files, mediaS3.uuid, cbctClinicId());
    } catch {
      done();
      addUserActionNotification({ message: "E_CONNECTION_FAILED", level: "error" });
      return;
    }

    const uploader = new FileUploader();
    uploader.on("finish", () => {
      done();
      setCbctDetails((prev) => {
        const next = { ...prev };
        pending.forEach((entry) => (next[entry.name] = { size: entry.size, uploadedAt: entry.uploadedAt }));
        return next;
      });
      setCbctFiles([...cbctFilesRef.current, ...files.map((file) => file.name)]);
    });
    uploader.on("error", () => {
      done();
      addUserActionNotification({ message: "E_CONNECTION_FAILED", level: "error" });
    });

    files.forEach((file) => uploader.upload(links[file.name], file));
  }

  function removeCbctFile(name: string) {
    setCbctFiles((mediaS3.files ?? []).filter((fileName) => fileName !== name));
  }

  function onDrop(accepted: File[]) {
    if (accepted.length === 0) return;

    let rejectedOther = false;
    const archives: File[] = [];

    for (const file of accepted) {
      const extension = fileExtensionOf(file.name);
      if (/^(jpg|jpeg|png|stl|pdf|ortho|mp4)$/.test(extension)) {
        acceptDrop(file);
      } else if (isHeicFile(file)) {
        void convertHeicToJpeg(file)
          .then(acceptDrop)
          .catch(() => addUserActionNotification({ message: "notify.upload.E_UNSUPPORTED_FORMAT_EXT", level: "error" }));
      } else if (acceptsCbct && isCbctArchive(file.name)) {
        archives.push(file);
      } else {
        rejectedOther = true;
      }
    }

    if (archives.length) {
      void uploadCbct(archives);
    }

    if (rejectedOther) {
      addUserActionNotification({ message: acceptsCbct ? "notify.upload.E_UNSUPPORTED_FORMAT_EXT_WITH_ARCHIVE" : "notify.upload.E_UNSUPPORTED_FORMAT_EXT", level: "error" });
    }
  }

  function removeSlot(id: string) {
    setSlotIds((prev) => prev.filter((slotId) => slotId !== id));
    setProgressById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    removeMediaItem(id);
  }

  function clearAllUploads() {
    uploadedSlots.forEach((id) => removeMediaItem(id));
    setSlotIds((prev) => prev.filter((id) => id in progressById));
    if (acceptsCbct) {
      setCbctFiles([]);
    }
  }

  const externalRecipeSlots = useMemo(() => Object.keys(media).filter((id) => id.startsWith("recipe_") && !slotIds.includes(id)), [media, slotIds]);

  useEffect(() => {
    setDetailsById((prev) => {
      let changed = false;
      const next = { ...prev };

      for (const id of externalRecipeSlots) {
        if (next[id]) continue;

        const file = media[id as TCorrectionMediaSemantics]?.img;
        if (file instanceof File) {
          next[id] = { size: file.size, uploadedAt: Date.now() };
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [externalRecipeSlots, media]);

  const visibleSlotIds = [...slotIds, ...externalRecipeSlots];
  const uploadedSlots = visibleSlotIds.filter((id) => media[id as TCorrectionMediaSemantics]?.user_filename && !(id in progressById));
  const uploadingSlots = slotIds.filter((id) => id in progressById);
  const cbctFiles = acceptsCbct ? (mediaS3.files ?? []) : [];
  cbctFilesRef.current = cbctFiles;

  return (
    <FadePresence isVisible={isVisible}>
      <div id="extra-media-block" className="form-body" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "1.25rem", width: "100%" }}>
        <div className="tw-flex tw-flex-col tw-gap-1">
          <h4 id="validation-files" className="block" style={{ fontWeight: 900, margin: 0 }}>
            <FormattedMessage id={titleId ?? "UPLOAD_FILES"} />
            {required ? <LabelRequiredIndicator /> : null}
          </h4>

          {subtitleId ? (
            <p className="tw-m-0 tw-text-sm tw-leading-5 tw-text-muted-foreground">
              <FormattedMessage id={subtitleId} />
            </p>
          ) : null}
        </div>

        <Dropzone
          onDrop={(files) => {
            setDropzoneDragging(false);
            onDrop(files);
          }}
          onDragEnter={() => setDropzoneDragging(true)}
          onDragLeave={() => setDropzoneDragging(false)}
          className="tw-w-full"
          style={{ width: "100%" }}
        >
          <FileDropzone testId="power-cap-upload-btn" dragging={dropzoneDragging} />
        </Dropzone>

        {existingFiles.length || uploadedSlots.length || uploadingSlots.length || cbctFiles.length || cbctPending.length ? (
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
              <span className="tw-text-sm tw-font-semibold tw-leading-5 tw-text-foreground">
                <FormattedMessage id="patient.files.uploaded_title" />
              </span>
              {uploadedSlots.length ? (
                <button
                  type="button"
                  className="tw-cursor-pointer tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-2 tw-py-[3px] tw-text-xs tw-leading-4 tw-text-[#404040] tw-underline"
                  onClick={clearAllUploads}
                >
                  <FormattedMessage id="patient.files.clear_all" />
                </button>
              ) : null}
            </div>
            {existingFiles.map((file) => (
              <ExistingUploadedFileRow key={file.linkto} patientId={patientId} file={file} />
            ))}
            {uploadedSlots.map((id) => {
              const fileName = media[id as TCorrectionMediaSemantics]?.user_filename ?? "";
              const photoFile = media[id as TCorrectionMediaSemantics]?.img as File | undefined;

              return (
                <div
                  key={id}
                  data-matomo-mask
                  data-hj-suppress
                  className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-bg-background tw-px-4 tw-py-3"
                >
                  {photoFile ? (
                    <button
                      type="button"
                      className="tw-flex tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"
                      onClick={() => setPreviewSlotId(id)}
                      data-testid={`power-cap-file-preview-${id}`}
                    >
                      <PowerCapFileThumbnail file={photoFile} />
                      <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
                        <span className="tw-truncate tw-text-xs tw-font-bold tw-leading-4 tw-text-foreground" title={fileName}>
                          {fileName}
                        </span>
                        <FileMetaLine detail={detailsById[id]} />
                      </span>
                    </button>
                  ) : (
                    <>
                      <FileTypeBadge extension={fileExtensionOf(fileName).toUpperCase()} />
                      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                        <span className="tw-truncate tw-text-xs tw-font-bold tw-leading-4 tw-text-foreground" title={fileName}>
                          {fileName}
                        </span>
                        <FileMetaLine detail={detailsById[id]} />
                      </span>
                    </>
                  )}
                  <button
                    type="button"
                    className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-border tw-bg-transparent tw-p-1 tw-text-foreground hover:tw-bg-muted"
                    onClick={() => removeSlot(id)}
                    aria-label={intl.formatMessage({ id: "FILE_UPLOAD_REMOVE" })}
                    data-testid={`power-cap-file-remove-${id}`}
                  >
                    <Trash2 className="tw-size-4" aria-hidden />
                  </button>
                </div>
              );
            })}

            {cbctFiles.map((fileName) => (
              <div
                key={fileName}
                data-matomo-mask
                data-hj-suppress
                className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-bg-background tw-px-4 tw-py-3"
              >
                <FileTypeBadge extension={fileExtensionOf(fileName).toUpperCase()} />
                <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                  <span className="tw-truncate tw-text-xs tw-font-bold tw-leading-4 tw-text-foreground" title={fileName}>
                    {fileName}
                  </span>
                  <FileMetaLine detail={cbctDetails[fileName]} />
                </span>
                <button
                  type="button"
                  className="tw-flex tw-size-6 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-border tw-bg-transparent tw-p-1 tw-text-foreground hover:tw-bg-muted"
                  onClick={() => removeCbctFile(fileName)}
                  aria-label={intl.formatMessage({ id: "FILE_UPLOAD_REMOVE" })}
                  data-testid={`power-cap-cbct-remove-${fileName}`}
                >
                  <Trash2 className="tw-size-4" aria-hidden />
                </button>
              </div>
            ))}

            {cbctPending.map((entry) => (
              <div key={entry.name} className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-bg-muted tw-px-4 tw-py-3">
                <Loader className="tw-size-4 tw-shrink-0 tw-animate-spin tw-text-muted-foreground" aria-hidden />
                <span className="tw-truncate tw-text-xs tw-leading-4 tw-text-muted-foreground" title={entry.name}>
                  {entry.name}
                </span>
              </div>
            ))}

            {uploadingSlots.map((id) => (
              <div key={id} className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-muted tw-px-3 tw-py-2">
                <Loader className="tw-size-4 tw-shrink-0 tw-animate-spin tw-text-muted-foreground" aria-hidden />
                <span className="tw-text-sm tw-text-muted-foreground">
                  <FormattedMessage id="FILE_UPLOAD_UPLOADING" />
                </span>
                <div className="tw-ml-auto tw-h-1 tw-w-16 tw-overflow-hidden tw-rounded-full tw-bg-muted">
                  <div className="tw-h-full tw-rounded-full tw-bg-brand-black tw-transition-[width]" style={{ width: `${Math.max(4, Math.min(100, progressById[id] ?? 0))}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {children}
      </div>

      <PowerCapFilePreviewDialog
        file={previewSlotId ? (media[previewSlotId as TCorrectionMediaSemantics]?.img as File | undefined) : undefined}
        fileName={previewSlotId ? (media[previewSlotId as TCorrectionMediaSemantics]?.user_filename ?? "") : ""}
        isOpen={previewSlotId !== null}
        onOpenChange={(open) => setPreviewSlotId(open ? previewSlotId : null)}
      />
    </FadePresence>
  );
}

/** Create and revoke the local object URL used to preview an unsubmitted file. */
function useObjectUrl(file: File | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return url;
}

function PowerCapFileThumbnail({ file }: { file: File }) {
  const previewUrl = useObjectUrl(file);

  if (!previewUrl) {
    return <FileText className="tw-size-10 tw-shrink-0 tw-text-muted-foreground" aria-hidden />;
  }

  return <img src={previewUrl} alt="" className="tw-size-10 tw-shrink-0 tw-rounded-md tw-object-cover" />;
}

function ExistingUploadedFileRow({ patientId, file }: { patientId: number; file: TPatientOptionalMedia }) {
  const fileName = file.linkto.split("/").at(-1) ?? file.linkto;
  const prefix = API_PATIENT_IMAGE(patientId, "");
  const hasThumbnail = Boolean(file.thumbnail) && !file.thumbnail.includes("THUMBNAIL");

  return (
    <a
      href={`${prefix}${file.linkto}`}
      download
      data-matomo-mask
      data-hj-suppress
      className="tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-bg-background tw-px-4 tw-py-3 tw-no-underline hover:tw-bg-muted"
      title={fileName}
    >
      {hasThumbnail ? (
        <img src={`${prefix}${file.thumbnail}`} alt="" className="tw-size-10 tw-shrink-0 tw-rounded-md tw-object-cover" />
      ) : (
        <FileTypeBadge extension={fileExtensionOf(fileName).toUpperCase()} />
      )}
      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
        <span className="tw-truncate tw-text-xs tw-font-bold tw-leading-4 tw-text-foreground">{fileName}</span>
        <FileMetaLine detail={{ size: file.media_size * 1024, uploadedAt: file.creation_date * 1000 }} />
      </span>
      <Download className="tw-size-4 tw-shrink-0 tw-text-foreground" aria-hidden />
    </a>
  );
}

function PowerCapFilePreviewDialog({ file, fileName, isOpen, onOpenChange }: { file: File | undefined; fileName: string; isOpen: boolean; onOpenChange(open: boolean): void }) {
  const previewUrl = useObjectUrl(file);

  return (
    <CenteredDialog isOpen={isOpen} onOpenChange={onOpenChange} closeProps={{}}>
      {previewUrl ? (
        <div className="tw-flex tw-max-h-[80vh] tw-flex-col tw-items-center tw-gap-4">
          <img src={previewUrl} alt={fileName} className="tw-max-h-[70vh] tw-max-w-full tw-rounded-lg tw-object-contain" />

          <a
            href={previewUrl}
            download={fileName}
            data-matomo-mask
            data-hj-suppress
            className="tw-flex tw-items-center tw-gap-2 tw-truncate tw-text-sm tw-text-foreground tw-no-underline hover:tw-underline"
            title={fileName}
          >
            <Download className="tw-size-4 tw-shrink-0" aria-hidden />
            {fileName}
          </a>
        </div>
      ) : null}
    </CenteredDialog>
  );
}

function FadePresence({ children, isVisible }: React.PropsWithChildren<{ isVisible: boolean }>) {
  const shouldReduceMotion = useReducedMotion();

  const animationProps: AnimationProps = {
    initial: { height: 0 },
    animate: { height: "auto", transition: { duration: 0.5 } },
    exit: { height: 0 },
  };

  return (
    <AnimatePresence>
      {isVisible ? (
        <motion.div className="tw-overflow-hidden [&_h4]:tw-mt-0 [&_h4]:tw-pt-0" {...(shouldReduceMotion ? {} : animationProps)}>
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

const connector = connect(mapStateToProps, mapDispatchToProps);

function fileExtensionOf(fileName: string): string {
  const extension = fileName.split(".").pop() ?? "";
  return extension && extension !== fileName ? extension.toLowerCase() : "";
}

function formatUploadedAt(timestamp: number): string {
  const date = new Date(timestamp);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${date.getHours()}:${pad(date.getMinutes())}`;
}

function FileMetaLine({ detail }: { detail?: { size: number; uploadedAt: number } }) {
  const intl = useIntl();
  if (!detail) {
    return null;
  }

  const sizeInMb = convertBytesToMB(detail.size);
  const sizeInKb = detail.size / 1024;
  const size = sizeInMb >= 0.1 ? sizeInMb : sizeInKb >= 1 ? sizeInKb : detail.size;
  const unitId = sizeInMb >= 0.1 ? "patient.files.size.mb" : sizeInKb >= 1 ? "patient.files.size.kb" : "patient.files.size.bytes";

  return (
    <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-leading-4 tw-text-muted-foreground">
      <span>
        {intl.formatNumber(size, { maximumFractionDigits: 1 })} {intl.formatMessage({ id: unitId })}
      </span>
      <span>•</span>
      <span>{formatUploadedAt(detail.uploadedAt)}</span>
    </span>
  );
}

function FileTypeBadge({ extension }: { extension: string }) {
  return (
    <span className="tw-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-[#dad7d2]" aria-hidden>
      <span className="tw-flex tw-h-[18px] tw-w-8 tw-items-center tw-justify-center tw-rounded tw-bg-[#78716c] tw-text-[11px] tw-font-bold tw-leading-4 tw-text-white">{extension}</span>
    </span>
  );
}

export default connector(PowerCapFileUpload);
